/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import React from 'react';

import type { HiveWorkingBranchState } from '@gitbook/hive-client';
import {
    applyRevisionComputations,
    type Revision,
    type RevisionComputations,
} from '@gitbook/hive-core';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { useHiveClient } from './useHiveClient';

type UseHiveComputedRevisionOptions = {
    /**
     * Suspend until complete computation is done.
     * When true, the hook will suspend the rendering until the first complete computation is finished.
     * @default false
     */
    suspendUntilComplete?: boolean;

    /**
     * Throw an error if the revision fails to compute.
     * @default false
     */
    throwOnError?: boolean;
};

/**
 * Compute a revision and return the computed revision.
 */
export function useHiveComputedRevision(
    sourceRevision: Revision,
    options?: UseHiveComputedRevisionOptions
): HiveWorkingBranchState;
export function useHiveComputedRevision(
    sourceRevision: null | undefined,
    options?: UseHiveComputedRevisionOptions
): null;
export function useHiveComputedRevision(
    sourceRevision: Revision | null | undefined,
    options?: UseHiveComputedRevisionOptions
): HiveWorkingBranchState | null;
export function useHiveComputedRevision(
    sourceRevision: Revision | null | undefined,
    options: UseHiveComputedRevisionOptions = {}
): HiveWorkingBranchState | null {
    const { suspendUntilComplete = false, throwOnError = false } = options ?? {};
    const client = useHiveClient();

    const contentComputer = sourceRevision
        ? client.getContentComputer(sourceRevision.contentId)
        : null;

    if (suspendUntilComplete && contentComputer && sourceRevision) {
        // When suspending until complete, we suspend the rendering until the complete computation is done.
        // This fills the computations cache and ensure the `computeRevision` below is called with a prefilled cache.
        getSuspense(
            contentComputer.getCompleteRevision(sourceRevision, {
                throwOnError,
            })
        );
    }

    const [computationsState, setComputationsState] = useStateWithDeps<RevisionComputations | null>(
        null,
        [sourceRevision]
    );

    // Ignore onUpdate from a superseded stream so its stale computations aren't applied
    // against a newer sourceRevision (which would throw in applyRevisionComputations).
    const activeStream = React.useRef<object | undefined>(undefined);
    const stream = React.useMemo(() => {
        if (!sourceRevision || !contentComputer) {
            return {
                result: null,
                cancel: () => {},
            };
        }

        const token = {};
        activeStream.current = token;

        return contentComputer.computeRevision({
            revision: sourceRevision,
            onUpdate: (computations) => {
                if (activeStream.current === token) {
                    setComputationsState(computations);
                }
            },
        });
    }, [contentComputer, sourceRevision, setComputationsState]);

    // Cancel the stream if it changes
    React.useEffect(() => {
        return () => {
            stream.cancel();
        };
    }, [stream]);

    const computations = computationsState ?? stream.result;
    const state = React.useMemo(() => {
        if (!computations || !sourceRevision) {
            return null;
        }

        const applyResult = applyRevisionComputations({
            revision: sourceRevision,
            computations,
        });

        if (throwOnError && applyResult.error) {
            throw applyResult.error;
        }

        return {
            revision: applyResult.result,
            sourceRevision,
            isComputed: applyResult.complete,
            computations,
        };
    }, [computations, sourceRevision, throwOnError]);

    return state;
}
